import { defineStore } from 'pinia'
import { reactive, computed } from 'vue'
import type { FormData, ValidationState, FieldValidation } from '@/types'
import { createInitialFormData } from '@/types'

export const useFormStore = defineStore('form', () => {
  // State
  const data = reactive<FormData>(createInitialFormData())

  const validation = reactive<ValidationState>({
    email: { valid: null, checking: false, error: null, sendToAC: true },
    phone: { valid: null, checking: false, error: null },
  })

  // Computed
  const fullName = computed(() => `${data.firstName} ${data.lastName}`.trim())

  const dob = computed(() => {
    if (data.dobMonth && data.dobDay && data.dobYear) {
      return `${data.dobMonth}/${data.dobDay}/${data.dobYear}`
    }
    return ''
  })

  const dobISO = computed(() => {
    if (data.dobMonth && data.dobDay && data.dobYear) {
      return `${data.dobYear}-${data.dobMonth.padStart(2, '0')}-${data.dobDay.padStart(2, '0')}`
    }
    return ''
  })

  const formattedDebtAmount = computed(() => {
    if (data.debtAmount === null) return ''
    if (data.debtAmount === 0) return 'Less than $5,000'
    if (data.debtAmount >= 100000) return 'More than $100,000'
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(data.debtAmount)
  })

  const isQualified = computed(() => {
    const debt = data.debtAmount || 0
    const income = parseInt(data.income || '0', 10)
    return debt >= 10000 && income >= 2000
  })

  // Actions
  function setField<K extends keyof FormData>(field: K, value: FormData[K]) {
    data[field] = value
    localStorage.setItem(`form_${field}`, JSON.stringify(value))
  }

  function setValidation(field: keyof ValidationState, state: Partial<FieldValidation>) {
    validation[field] = { ...validation[field], ...state }
  }

  function restoreFromStorage() {
    const fields: (keyof FormData)[] = [
      'debtAmount', 'debtAmountRaw', 'income',
      'firstName', 'lastName', 'email', 'phone', 'phoneUnmasked',
      'address', 'city', 'state', 'zip',
      'dobMonth', 'dobDay', 'dobYear',
      'advertiserId', 'revenue', 'redirectUrl',
    ]

    fields.forEach(field => {
      const saved = localStorage.getItem(`form_${field}`)
      if (saved) {
        try {
          (data as any)[field] = JSON.parse(saved)
        } catch {
          (data as any)[field] = saved
        }
      }
    })
  }

  function clearStorage() {
    Object.keys(data).forEach(key => {
      localStorage.removeItem(`form_${key}`)
    })
  }

  function reset() {
    Object.assign(data, createInitialFormData())
    validation.email = { valid: null, checking: false, error: null, sendToAC: true }
    validation.phone = { valid: null, checking: false, error: null }
    clearStorage()
  }

  return {
    // State
    data,
    validation,
    // Computed
    fullName,
    dob,
    dobISO,
    formattedDebtAmount,
    isQualified,
    // Actions
    setField,
    setValidation,
    restoreFromStorage,
    reset,
  }
})
